import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import swal from 'sweetalert';
import SubscriptionService from '../../../services/SubscriptionService';
import { PageHeader, Card, Button, Badge, StatTile, Field, Input, Select, Table, Tr, Td, EmptyState } from '../../ui';

const money = (c, cur = 'cad') =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: (cur || 'cad').toUpperCase() }).format((c || 0) / 100);
const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(isNaN(Number(d)) ? d : Number(d));
  return isNaN(dt) ? '—' : dt.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
};
const toDateInput = (d) => {
  if (!d) return '';
  const dt = new Date(isNaN(Number(d)) ? d : Number(d));
  return isNaN(dt) ? '' : dt.toISOString().slice(0, 10);
};
const statusTone = { active: 'good', trialing: 'info', expiring_soon: 'warn', canceling: 'warn', past_due: 'live', canceled: 'neutral', expired: 'neutral', inactive: 'neutral' };

const AdminSubscriptions = () => {
  const [tab, setTab] = useState('subs');
  const [subs, setSubs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [q, setQ] = useState('');
  const [msg, setMsg] = useState(null);

  const [detail, setDetail] = useState(null); // { subscription, user, payments, refunds, disputes }
  const [edit, setEdit] = useState(null);     // editable form for the open row
  const [busy, setBusy] = useState(false);

  const flash = (ok, text) => { setMsg({ ok, text }); setTimeout(() => setMsg(null), 4500); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        SubscriptionService.getAllSubscriptions({ status: statusFilter, q: q || undefined }),
        SubscriptionService.getAllPlans().catch(() => ({ plans: [] })),
      ]);
      setSubs(s.subscriptions || []);
      setPlans(p.plans || []);
    } catch (e) {
      flash(false, e.error || 'Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (tab === 'disputes' && !disputes.length) {
      SubscriptionService.getDisputes().then((r) => setDisputes(r.disputes || [])).catch(() => {});
    }
  }, [tab, disputes.length]);

  const openDetail = async (row) => {
    setDetail({ loading: true });
    try {
      const d = await SubscriptionService.getSubscriptionDetail(row.id);
      setDetail(d);
      setEdit({
        plan_id: d.subscription.plan_id,
        status: d.subscription.status,
        current_period_end: toDateInput(d.subscription.current_period_end),
        cancel_at_period_end: !!d.subscription.cancel_at_period_end,
        extendDays: 30,
        refundAmount: '',
        refundReason: 'requested_by_customer',
        refundNote: '',
      });
    } catch (e) {
      setDetail(null);
      flash(false, e.error || 'Failed to load subscription');
    }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await SubscriptionService.updateSubscription(detail.subscription.id, {
        plan_id: edit.plan_id,
        status: edit.status,
        current_period_end: edit.current_period_end || undefined,
        cancel_at_period_end: edit.cancel_at_period_end,
      });
      flash(true, 'Subscription updated');
      setDetail(null); load();
    } catch (e) { flash(false, e.error || 'Update failed'); }
    finally { setBusy(false); }
  };

  const doExtend = async () => {
    setBusy(true);
    try {
      const r = await SubscriptionService.extendSubscription(detail.subscription.id, Number(edit.extendDays));
      flash(true, r.message || 'Extended');
      setDetail(null); load();
    } catch (e) { flash(false, e.error || 'Extend failed'); }
    finally { setBusy(false); }
  };

  const doCancel = (atPeriodEnd) => {
    swal({
      title: atPeriodEnd ? 'Cancel at period end?' : 'Cancel immediately?',
      text: atPeriodEnd
        ? 'The subscription stays active until the current period ends, then lapses.'
        : 'Access is revoked now. This does not issue a refund.',
      icon: 'warning', buttons: ['Back', 'Confirm'], dangerMode: true,
    }).then(async (ok) => {
      if (!ok) return;
      setBusy(true);
      try {
        await SubscriptionService.cancelSubscriptionAdmin(detail.subscription.id, atPeriodEnd);
        flash(true, 'Subscription canceled');
        setDetail(null); load();
      } catch (e) { flash(false, e.error || 'Cancel failed'); }
      finally { setBusy(false); }
    });
  };

  const doRefund = async () => {
    const cents = edit.refundAmount ? Math.round(parseFloat(edit.refundAmount) * 100) : undefined;
    setBusy(true);
    try {
      const r = await SubscriptionService.refundSubscription(detail.subscription.id, {
        amount: cents, reason: edit.refundReason, note: edit.refundNote || undefined,
      });
      flash(true, r.message || 'Refund issued');
      setDetail(null); load();
    } catch (e) { flash(false, e.error || 'Refund failed'); }
    finally { setBusy(false); }
  };

  const counts = subs.reduce((a, s) => { a[s.status] = (a[s.status] || 0) + 1; return a; }, {});
  const mrr = subs.filter((s) => s.status === 'active')
    .reduce((sum, s) => sum + (s.price_monthly || 0), 0);

  return (
    <>
      <PageHeader title="Subscriptions" subtitle="Who's on what plan — edit, extend, cancel, refund, and track disputes" />

      {msg && (
        <div className={`tw-mb-4 tw-rounded-xl tw-px-4 tw-py-2.5 tw-text-sm ${msg.ok ? 'tw-bg-good/10 tw-text-good' : 'tw-bg-live/10 tw-text-live'}`}>
          {msg.text}
        </div>
      )}

      <div className="tw-mb-5 tw-grid tw-gap-3 sm:tw-grid-cols-3 lg:tw-grid-cols-4">
        <StatTile icon="fa-user-check" label="Active" value={counts.active || 0} tone="good" />
        <StatTile icon="fa-hourglass-half" label="Canceling / past due" value={(counts.canceling || 0) + (counts.past_due || 0)} tone="warn" />
        <StatTile icon="fa-ban" label="Canceled / expired" value={(counts.canceled || 0) + (counts.expired || 0)} tone="neutral" />
        <StatTile icon="fa-money-bill-wave" label="Monthly plan value" value={money(mrr)} tone="brand" />
      </div>

      <div className="tw-mb-4 tw-flex tw-gap-2">
        {[['subs', 'Subscriptions'], ['disputes', 'Disputes & chargebacks']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`tw-rounded-lg tw-px-3.5 tw-py-1.5 tw-text-sm tw-font-semibold tw-transition ${tab === k ? 'tw-bg-brand tw-text-white' : 'tw-bg-sunken tw-text-content-muted hover:tw-text-heading'}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'subs' && (
        <Card noPad>
          <div className="tw-flex tw-flex-wrap tw-gap-2 tw-p-4">
            <Input className="tw-max-w-xs" placeholder="Search email, name, plan…" value={q}
              onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} />
            <Select className="tw-max-w-[180px]" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              {['all', 'active', 'canceling', 'past_due', 'canceled', 'expired', 'inactive'].map((s) => (
                <option key={s} value={s}>{s === 'all' ? 'All statuses' : s}</option>
              ))}
            </Select>
            <Button variant="secondary" icon="fa-rotate" onClick={load}>Refresh</Button>
          </div>

          {loading ? (
            <div className="tw-py-16 tw-text-center"><i className="fa-solid fa-spinner fa-spin tw-text-2xl tw-text-brand" /></div>
          ) : subs.length === 0 ? (
            <EmptyState icon="fa-inbox" title="No subscriptions" hint="Nothing matches the current filter." />
          ) : (
            <Table head={['Customer', 'Plan', 'Status', 'Started', 'Renews / ends', 'Paid', 'Refunded', '']}>
              {subs.map((s) => (
                <Tr key={s.id} onClick={() => openDetail(s)}>
                  <Td>
                    <div className="tw-font-semibold tw-text-heading">{s.full_name || s.username || '—'}</div>
                    <div className="tw-text-xs tw-text-content-faint">{s.email}</div>
                  </Td>
                  <Td className="tw-text-content-muted">{s.plan_name}</Td>
                  <Td>
                    <Badge tone={statusTone[s.status] || 'neutral'}>{s.status}</Badge>
                    {!!s.cancel_at_period_end && s.status === 'active' && (
                      <span className="tw-ml-1.5 tw-text-[10px] tw-text-warn">ending</span>
                    )}
                  </Td>
                  <Td className="tw-text-content-muted">{fmtDate(s.created_at)}</Td>
                  <Td className="tw-text-content-muted">{fmtDate(s.current_period_end)}</Td>
                  <Td className="tw-font-semibold tw-text-heading">{s.last_payment_amount != null ? money(s.last_payment_amount) : '—'}</Td>
                  <Td className="tw-text-content-muted">{s.refunded_total ? money(s.refunded_total) : '—'}</Td>
                  <Td className="tw-text-right"><i className="fa-solid fa-chevron-right tw-text-xs tw-text-content-faint" /></Td>
                </Tr>
              ))}
            </Table>
          )}
        </Card>
      )}

      {tab === 'disputes' && (
        <Card noPad>
          {disputes.length === 0 ? (
            <EmptyState icon="fa-scale-balanced" title="No disputes" hint="Chargebacks and payment disputes from Stripe show up here." />
          ) : (
            <Table head={['Opened', 'Customer', 'Amount', 'Reason', 'Status', 'Respond by', '']}>
              {disputes.map((d) => (
                <Tr key={d.stripe_dispute_id}>
                  <Td className="tw-text-content-muted">{fmtDate(d.created_at)}</Td>
                  <Td>{d.email || d.username || <span className="tw-text-content-faint">unknown</span>}</Td>
                  <Td className="tw-font-semibold tw-text-heading">{money(d.amount, d.currency)}</Td>
                  <Td className="tw-text-content-muted">{(d.reason || '').replace(/_/g, ' ')}</Td>
                  <Td><Badge tone={/won|warning_closed/.test(d.status) ? 'good' : /lost|closed/.test(d.status) ? 'live' : 'warn'}>{d.status}</Badge></Td>
                  <Td className="tw-text-content-muted">{d.evidence_due_by ? fmtDate(d.evidence_due_by) : '—'}</Td>
                  <Td className="tw-text-right">
                    <a href={`https://dashboard.stripe.com/disputes/${d.stripe_dispute_id}`} target="_blank" rel="noreferrer"
                      className="tw-text-xs tw-font-semibold tw-text-brand">Open in Stripe</a>
                  </Td>
                </Tr>
              ))}
            </Table>
          )}
        </Card>
      )}

      {/* Detail / manage modal */}
      <Modal show={!!detail} onHide={() => setDetail(null)} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="tw-text-base">Manage subscription</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!detail || detail.loading || !edit ? (
            <div className="tw-py-10 tw-text-center"><i className="fa-solid fa-spinner fa-spin tw-text-xl tw-text-brand" /></div>
          ) : (
            <div className="tw-space-y-5">
              <div className="tw-rounded-xl tw-bg-sunken tw-p-3 tw-text-sm">
                <div className="tw-font-semibold tw-text-heading">{detail.user?.full_name || detail.user?.username}</div>
                <div className="tw-text-content-faint">{detail.user?.email}</div>
                <div className="tw-mt-1 tw-text-xs tw-text-content-muted">Subscription #{detail.subscription.id} · created {fmtDate(detail.subscription.created_at)}</div>
              </div>

              <section className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
                <Field label="Plan">
                  <Select value={edit.plan_id} onChange={(e) => setEdit((p) => ({ ...p, plan_id: Number(e.target.value) }))}>
                    {plans.map((pl) => <option key={pl.id} value={pl.id}>{pl.name} — {money(pl.price_monthly)}/mo</option>)}
                  </Select>
                </Field>
                <Field label="Status">
                  <Select value={edit.status} onChange={(e) => setEdit((p) => ({ ...p, status: e.target.value }))}>
                    {['active', 'past_due', 'canceled', 'expired', 'inactive'].map((s) => <option key={s} value={s}>{s}</option>)}
                  </Select>
                </Field>
                <Field label="Current period ends">
                  <Input type="date" value={edit.current_period_end}
                    onChange={(e) => setEdit((p) => ({ ...p, current_period_end: e.target.value }))} />
                </Field>
                <label className="tw-flex tw-items-center tw-gap-2 tw-self-end tw-pb-2 tw-text-sm tw-text-content-muted">
                  <input type="checkbox" checked={edit.cancel_at_period_end}
                    onChange={(e) => setEdit((p) => ({ ...p, cancel_at_period_end: e.target.checked }))} />
                  Cancel at period end
                </label>
              </section>
              <Button onClick={saveEdit} disabled={busy} icon="fa-floppy-disk">Save changes</Button>

              <section className="tw-rounded-xl tw-border tw-border-line tw-p-4">
                <div className="tw-mb-2 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-content-faint">Quick actions</div>
                <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-2">
                  <Field label="Extend by (days)">
                    <Input type="number" className="tw-w-28" value={edit.extendDays}
                      onChange={(e) => setEdit((p) => ({ ...p, extendDays: e.target.value }))} />
                  </Field>
                  <Button variant="secondary" onClick={doExtend} disabled={busy} icon="fa-calendar-plus">Extend</Button>
                  <span className="tw-flex-1" />
                  <Button variant="ghost" onClick={() => doCancel(true)} disabled={busy}>Cancel at period end</Button>
                  <Button variant="danger" onClick={() => doCancel(false)} disabled={busy} icon="fa-ban">Cancel now</Button>
                </div>
              </section>

              <section className="tw-rounded-xl tw-border tw-border-line tw-p-4">
                <div className="tw-mb-2 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-content-faint">Refund</div>
                <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-2">
                  <Field label="Amount ($)" hint="Blank = full refund of the latest payment">
                    <Input type="number" step="0.01" className="tw-w-32" value={edit.refundAmount}
                      onChange={(e) => setEdit((p) => ({ ...p, refundAmount: e.target.value }))} />
                  </Field>
                  <Field label="Reason">
                    <Select value={edit.refundReason} onChange={(e) => setEdit((p) => ({ ...p, refundReason: e.target.value }))}>
                      <option value="requested_by_customer">Requested by customer</option>
                      <option value="duplicate">Duplicate</option>
                      <option value="fraudulent">Fraudulent</option>
                    </Select>
                  </Field>
                  <Button variant="danger" onClick={doRefund} disabled={busy} icon="fa-rotate-left">Issue refund</Button>
                </div>
                <Input className="tw-mt-2" placeholder="Internal note (optional)" value={edit.refundNote}
                  onChange={(e) => setEdit((p) => ({ ...p, refundNote: e.target.value }))} />
              </section>

              {!!detail.payments?.length && (
                <section>
                  <div className="tw-mb-2 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-content-faint">Payments</div>
                  <Table head={['Date', 'Amount', 'Status', 'Ref']}>
                    {detail.payments.map((p) => (
                      <Tr key={p.id}>
                        <Td className="tw-text-content-muted">{fmtDate(p.created_at)}</Td>
                        <Td className="tw-font-semibold tw-text-heading">{money(p.amount, p.currency)}</Td>
                        <Td><Badge tone={/succ|paid|complete/.test(p.status) ? 'good' : 'neutral'}>{p.status}</Badge></Td>
                        <Td className="tw-font-mono tw-text-[11px] tw-text-content-faint">{p.stripe_payment_intent_id || '—'}</Td>
                      </Tr>
                    ))}
                  </Table>
                </section>
              )}

              {!!detail.refunds?.length && (
                <section>
                  <div className="tw-mb-2 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-content-faint">Refunds</div>
                  <Table head={['Date', 'Amount', 'Reason', 'Status']}>
                    {detail.refunds.map((r) => (
                      <Tr key={r.id}>
                        <Td className="tw-text-content-muted">{fmtDate(r.created_at)}</Td>
                        <Td className="tw-font-semibold tw-text-heading">{money(r.amount, r.currency)}</Td>
                        <Td className="tw-text-content-muted">{(r.reason || '').replace(/_/g, ' ')}</Td>
                        <Td><Badge tone={r.status === 'succeeded' ? 'good' : 'warn'}>{r.status}</Badge></Td>
                      </Tr>
                    ))}
                  </Table>
                </section>
              )}

              {!!detail.disputes?.length && (
                <div className="tw-rounded-xl tw-bg-live/10 tw-p-3 tw-text-sm tw-text-live">
                  <i className="fa-solid fa-triangle-exclamation tw-mr-1.5" />
                  {detail.disputes.length} open dispute(s) on this customer — see the Disputes tab.
                </div>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AdminSubscriptions;
